import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { ChevronRight, Loader2, Plus } from 'lucide-react';

import { formatNovaScheduleListTiming, formatNovaScheduleStatus } from './nova-schedule-model';
import { groupNovaSchedulesByAccount } from './nova-schedule-model';
import type { NovaScheduleRecord } from '../../../../queries/nova-schedule-management';
import { novaGradientClass } from '../../design-system/gradients';

export function NovaSchedulesList({
  schedules,
  isLoading,
  error,
  onNewSchedule,
  onRetry,
  onSelectSchedule,
}: {
  schedules: NovaScheduleRecord[];
  isLoading: boolean;
  error?: unknown;
  onNewSchedule: () => void;
  onRetry: () => void;
  onSelectSchedule: (scheduleUuid: string) => void;
}) {
  const groups = groupNovaSchedulesByAccount(schedules);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 bg-white">
      <div className="flex w-full items-center gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-steel-500 text-[17px] leading-6">Scheduled</h2>
          <p className="text-steel-400 mt-1 text-[12px] leading-4">
            Work Nova prepares for you on a recurring cadence.
          </p>
        </div>
        <button
          type="button"
          onClick={onNewSchedule}
          className={cn(
            'text-wtw-ultraviolet-850 inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full',
            'px-4 text-[14px] leading-5 font-semibold',
            novaGradientClass.aiBackground,
          )}
        >
          <Plus aria-hidden="true" className="size-[18px]" />
          New schedule
        </button>
      </div>

      {isLoading ? (
        <div className="text-steel-400 flex items-center justify-center py-12 text-sm">
          <Loader2 aria-hidden="true" className="mr-2 size-5 animate-spin" />
          Loading schedules...
        </div>
      ) : error ? (
        <div className="border-steel-75 rounded-[4px] border bg-white p-4">
          <p className="text-[14px] text-[#C5342A]">Unable to load your schedules.</p>
          <Button type="button" className="mt-3" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <section key={group.key} className="border-steel-75 overflow-hidden rounded-[4px] border bg-white">
              <div className="bg-steel-50 border-steel-75 flex items-center justify-between border-b px-[14px] py-[10px]">
                <h3 className="text-steel-500 text-[12px] leading-4 font-semibold">{group.name}</h3>
                <span className="text-steel-400 text-[12px] leading-4">{group.schedules.length} scheduled</span>
              </div>
              <div className="divide-steel-75 divide-y">
                {group.schedules.map((schedule) => (
                  <NovaScheduleListItem
                    key={schedule.uuid}
                    schedule={schedule}
                    onClick={() => onSelectSchedule(schedule.uuid)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function NovaScheduleListItem({ schedule, onClick }: { schedule: NovaScheduleRecord; onClick: () => void }) {
  const status = schedule.status.status;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'hover:bg-steel-50 focus-visible:ring-brand-400 group flex w-full flex-col gap-[10px] p-4 text-left',
        'transition-colors focus-visible:ring-2 focus-visible:outline-hidden focus-visible:ring-inset',
      )}
    >
      <span className="flex w-full items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="text-steel-500 truncate text-[14px] leading-5 font-semibold">{schedule.title}</span>
          {status !== 'ACTIVE' ? <NovaScheduleStatusBadge status={status} /> : null}
        </span>
        <ChevronRight aria-hidden="true" className="text-steel-200 group-hover:text-brand-400 size-4 shrink-0" />
      </span>
      <span className="text-steel-400 text-[12px] leading-4">{formatNovaScheduleListTiming(schedule)}</span>
      <span className="text-steel-500 line-clamp-2 text-[12px] leading-4">{schedule.prompt}</span>
    </button>
  );
}

export function NovaScheduleStatusBadge({ status }: { status: NovaScheduleRecord['status']['status'] }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-[6px] px-2 py-1 text-[12px] leading-4',
        status === 'PAUSED'
          ? 'bg-[#FFFCF0] text-[#FF4773]'
          : status === 'COMPLETED'
            ? 'bg-steel-50 text-steel-400'
            : 'bg-[#F4F8FE] text-[#0957C3]',
      )}
    >
      {formatNovaScheduleStatus(status)}
    </span>
  );
}
